"use client";
import { motion } from "framer-motion";
import Image from "next/image";
import React from "react";
import { Typewriter } from "react-simple-typewriter";
import SectionTitle from "./SectionTitle";

type CommentSectionProps = {
  sectionTitle?: string; // e.g. "Client Quote."
  quote: string; // main testimonial text
  avatarSrc: string; // image path
  name: string; // client name
  rating?: number; // default 5
  maxRating?: number; // default 5
};

const CommentSection: React.FC<CommentSectionProps> = ({
  sectionTitle = "Client Quote.",
  quote,
  name,
  rating = 5,
  maxRating = 5,
}) => {



  return (
    <section className="w-full py-12 xl:py-0">
      <div className="flex flex-col xl:flex-row items-stretch">
        {/* Left Panel */}
        <div className="w-full xl:w-[35%]">
          <div className="mx-auto custom-container px-4 xl:ml-48 flex flex-col justify-center items-start h-full">
            <SectionTitle title={sectionTitle} align="left" />
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-full xl:w-[65%] flex relative xl:py-12">
          {/* Diagonal Background (XL only) */}
          <div className=" hidden xl:flex items-center justify-center">
            <div
              className="hidden xl:block absolute top-0 left-0 w-full h-full comment-section-background"
              style={{
                clipPath: "polygon(8.666% 0%, 100% 0%, 100% 100%, 0% 100%)",
                zIndex: 0,
              }}
            ></div>

            {/* decorative svg */}
            <div className="absolute  xl:-left-24 mr-32 top-0">
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="280"
                height="384"
                viewBox="0 0 280 384"
                fill="none"
              >
                <g id="QuoteFrame">
                  <g id="Group 1321314691">
                    <g id="Rectangle 34624418" filter="url(#filter0_d_487_350)">
                      <path
                        d="M129.083 127.5H143.999L84.9609 328H70.041L129.083 127.5Z"
                        fill="#D53333"
                      />
                    </g>
                    <g id="Group 1321314692">
                      <g id="Rectangle 34624418_2" filter="url(#filter1_d_487_350)">
                        <path
                          d="M107.388 127.5H122.304L63.2656 328H48.3457L107.388 127.5Z"
                          fill="#D53333"
                        />
                      </g>
                    </g>
                  </g>

                  {/* ✅ Animate only this path */}
                  <motion.path
                    id="Quotes"
                    d="M214.657 47.2119V23.5H178.257V53.6602H196.561C196.561 61.7026 194.412 68.22 190.113 73.2119C185.815 78.0653 179.574 80.8389 171.393 81.5322L168.481 97.3398C176.138 96.9368 183.404 95.1027 190.28 91.8369L190.945 91.5156C198.017 88.049 203.702 82.7105 208.001 75.5C212.3 68.5147 214.516 59.4474 214.65 48.2979L214.657 47.2119ZM266.657 47.2119V23.5H230.257V53.6602H248.561C248.561 61.7026 246.412 68.22 242.113 73.2119C237.815 78.0653 231.574 80.8389 223.393 81.5322L220.481 97.3398C228.138 96.9368 235.404 95.1027 242.28 91.8369L242.945 91.5156C250.017 88.049 255.702 82.7105 260.001 75.5C264.3 68.5147 266.516 59.4474 266.65 48.2979L266.657 47.2119ZM215.282 47.2119C215.282 58.9297 213.051 68.4807 208.537 75.8193L208.538 75.8203C204.182 83.1277 198.409 88.5536 191.221 92.0771C184.073 95.5811 176.501 97.5444 168.514 97.9648L167.723 98.0059L170.778 81.4189L170.864 80.9492L171.34 80.9092C179.406 80.2256 185.479 77.5002 189.645 72.7969C193.711 68.0712 195.822 61.9188 195.931 54.2852H177.632V22.875H215.282V47.2119ZM267.282 47.2119C267.282 58.9297 265.051 68.4807 260.537 75.8193L260.538 75.8203C256.182 83.1277 250.409 88.5536 243.221 92.0771C236.073 95.5811 228.501 97.5444 220.514 97.9648L219.723 98.0059L222.778 81.4189L222.864 80.9492L223.34 80.9092C231.406 80.2256 237.479 77.5002 241.645 72.7969C245.711 68.0712 247.822 61.9188 247.931 54.2852H229.632V22.875H267.282V47.2119Z"
                    stroke="#CCCCCC"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse", // or "loop" if you want reset effect
                      ease: "easeInOut",
                    }}
                  />
                </g>

                <defs>
                  <filter
                    id="filter0_d_487_350"
                    x="43.041"
                    y="81.5"
                    width="175.959"
                    height="302.5"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dx="24" dy="5" />
                    <feGaussianBlur stdDeviation="25.5" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0.416721 0 0 0 0 0.501976 0 0 0 0 0.581731 0 0 0 0.41 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_487_350"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_487_350"
                      result="shape"
                    />
                  </filter>
                  <filter
                    id="filter1_d_487_350"
                    x="21.3457"
                    y="81.5"
                    width="175.959"
                    height="302.5"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dx="24" dy="5" />
                    <feGaussianBlur stdDeviation="25.5" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0.416721 0 0 0 0 0.501976 0 0 0 0 0.581731 0 0 0 0.41 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_487_350"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_487_350"
                      result="shape"
                    />
                  </filter>
                </defs>
              </motion.svg>
            </div>
          </div>

          {/* 👉 Add a simple background below xl */}
          <div className="absolute inset-0 bg-gray-50 xl:hidden" />

          {/* Content */}
          <div className="custom-container xl:max-w-2xl 2xl:max-w-3xl ml-auto xl:ml-48 2xl:ml-auto mx-auto relative z-10 flex flex-col justify-start items-start gap-8 h-full py-8 p-4">

            <p className="xl:text-xl text-lg text-[#1b1b1b] min-h-[110px] leading-tight">

              <Typewriter
                words={[quote]}
                typeSpeed={5}
                deleteSpeed={30}
                delaySpeed={1000} // how long before it loops
                cursor
                cursorStyle="|"
              />

            </p>

            {/* Author & Rating */}
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="xl:size-16 size-12 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={"/avatars/ai-avtar.svg"}
                  alt={name}
                  width={128}
                  height={128}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Details */}
              <div>
                <div className="flex items-center gap-3">
                  <span className="xl:text-lg text-base font-semibold text-gray-800">
                    {rating.toFixed(1)} / {maxRating}
                  </span>
                  <div className="flex text-orange-400">
                    {[...Array(maxRating)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${i < rating ? "fill-current blink-star" : "fill-gray-300"
                          }`}
                        style={{ animationDelay: `${i * 0.2}s` }} // staggered blinking
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>

                <div className="text-lg text-gray-900">{name}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommentSection;
